import { useEffect } from "react";
import { Copy, Instagram, Share2, RefreshCcw, Save, Download, X, Maximize2 } from "lucide-react";
import Markdown from "@/components/Markdown";
import CarouselArtifact from "@/components/agent/CarouselArtifact";
import TablesBar from "@/components/shared/TablesBar";
import { hasInstagramCaption } from "@/lib/caption";
import { isSavedWithRevision } from "@/lib/tables";

const canWebShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

export function ArtifactToolbar({
  artifact,
  onCopy,
  onInstagram,
  onShare,
  onRedo,
  onSave,
  onPdf,
  onExpand,
  pdfLoading = false,
  viewer = false,
  sending = false,
  saving = false,
  compact = false,
}) {
  const disabled = !artifact;
  const saveRevision = isSavedWithRevision(artifact);
  const saveLabel = saving
    ? (saveRevision ? "Salvando ajustes…" : "Salvando…")
    : (saveRevision ? "Salvar ajustes" : "Salvar");
  const showInstagram = Boolean(onInstagram) && hasInstagramCaption(artifact?.content);
  const btn = compact
    ? "p-2 rounded-full hover:bg-brand-elevated text-brand-ink2 hover:text-brand-rose disabled:opacity-40"
    : "p-2.5 rounded-full hover:bg-brand-elevated text-brand-ink2 hover:text-brand-rose disabled:opacity-40 min-h-[44px] min-w-[44px] inline-flex items-center justify-center";
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="artifact-toolbar">
      <button type="button" data-testid="artifact-copy" onClick={onCopy} disabled={disabled} className={btn} title="Copiar" aria-label="Copiar">
        <Copy className="w-4 h-4" />
      </button>
      {showInstagram && (
        <button type="button" data-testid="artifact-copy-instagram" onClick={onInstagram} disabled={disabled} className={btn} title="Copiar legenda" aria-label="Copiar legenda">
          <Instagram className="w-4 h-4" />
        </button>
      )}
      {canWebShare && (
        <button type="button" data-testid="artifact-share-instagram" onClick={onShare} disabled={disabled} className={btn} title="Compartilhar" aria-label="Compartilhar">
          <Share2 className="w-4 h-4" />
        </button>
      )}
      {!viewer && onRedo && (
        <button type="button" data-testid="artifact-refaz" onClick={onRedo} disabled={disabled || sending} className={btn} title="Refazer" aria-label="Refazer">
          <RefreshCcw className="w-4 h-4" />
        </button>
      )}
      {!viewer && onSave && (
        <button type="button" data-testid="artifact-save" onClick={onSave} disabled={disabled || (artifact?.status === "saved" && !saveRevision) || sending || saving} aria-busy={saving || undefined} className={btn} title={saveLabel} aria-label={saveLabel}>
          <Save className="w-4 h-4" />
        </button>
      )}
      {artifact?.status === "saved" && onPdf && (
        <button type="button" data-testid="artifact-pdf" onClick={onPdf} disabled={pdfLoading} className={btn} title="Baixar PDF" aria-label="Baixar PDF">
          <Download className="w-4 h-4" />
        </button>
      )}
      {onExpand && (
        <button type="button" data-testid="artifact-expand" onClick={onExpand} disabled={disabled} className={btn} title="Tela cheia" aria-label="Tela cheia">
          <Maximize2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default function ArtifactStage({
  open,
  onClose,
  artifact,
  onCopy,
  onInstagram,
  onShare,
  onRedo,
  onSave,
  onPdf,
  pdfLoading,
  viewer,
  sending,
  saving = false,
  working = false,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-brand-bg flex flex-col"
      data-testid="artifact-stage"
      role="dialog"
      aria-modal="true"
      aria-label={artifact?.title || "Artefato"}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-2 px-4 sm:px-8 py-4 border-b border-brand-line">
        <div className="min-w-0 col-start-1 row-start-1">
          <div className="text-[10.5px] tracking-[0.22em] uppercase text-brand-ink2 font-ui">
            Artefato {artifact?.status === "saved" && <span className="text-brand-sage">(salvo)</span>}
          </div>
          <h2 className="font-display text-xl sm:text-2xl text-brand-ink break-words [overflow-wrap:anywhere]">
            {artifact?.title || "Aguardando geração"}
          </h2>
        </div>
        <div className="col-span-2 sm:col-span-1 sm:col-start-2 row-start-2 sm:row-start-1 min-w-0">
          <ArtifactToolbar
            artifact={artifact}
            onCopy={onCopy}
            onInstagram={onInstagram}
            onShare={onShare}
            onRedo={onRedo}
            onSave={onSave}
            onPdf={onPdf}
            pdfLoading={pdfLoading}
            viewer={viewer}
            sending={sending}
            saving={saving}
          />
        </div>
        <button
          type="button"
          data-testid="artifact-stage-close"
          onClick={onClose}
          className="col-start-2 sm:col-start-3 row-start-1 p-2.5 rounded-full hover:bg-brand-elevated text-brand-ink2 hover:text-brand-ink min-h-[44px] min-w-[44px] inline-flex items-center justify-center justify-self-end"
          aria-label="Fechar tela cheia"
          title="Fechar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <TablesBar artifact={artifact} working={working} />
      <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-soft">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8">
          {artifact ? (
            artifact.type === "carrossel" ? (
              <CarouselArtifact content={artifact.content} />
            ) : (
              <Markdown text={artifact.content} />
            )
          ) : (
            <p className="text-brand-ink2 text-[14px]">O conteúdo gerado vai aparecer aqui.</p>
          )}
        </div>
      </div>
    </div>
  );
}
